import React from "react";
import { Helmet } from "react-helmet";
import {
  ImageFragment,
  Maybe,
  OpenGraphMetaTagsFragment,
  SanityOpenGraph
} from "types/graphql-types";
import { useLanguage } from "../../utils/localization/languages/I18nContext";
import { Language } from "src/utils/localization/languages/types";
import {
  isSanityImagePresent,
  useSanityClient
} from "@silversea-ssc/ui-components-react";
import { sanityImageUrl } from "@silversea-ssc/ui-components-react";
import sanityImageUrlBuilder from "@sanity/image-url";
import { ImageUrlBuilder } from "@sanity/image-url/lib/types/builder";

export interface OpenGraphProps {
  openGraph?: Maybe<SanityOpenGraph> | Maybe<OpenGraphMetaTagsFragment>;
  altTitle?: Maybe<string>;
  altDescription?: Maybe<string>;
  altImage?: Maybe<string>;
  language?: Language;
  excludeFromSitemap?: Maybe<boolean>;
}

const buildOpenGraphImageUrl = (
  builder: ImageUrlBuilder,
  image: ImageFragment | undefined
): string | undefined => {
  if (isSanityImagePresent(image))
    return sanityImageUrl(builder, image.asset, { width: 1200, height: 630 });

  return undefined;
};

const OpenGraph: React.FC<OpenGraphProps> = ({
  children,
  openGraph,
  language,
  altTitle,
  altDescription,
  altImage,
  excludeFromSitemap = false
}) => {
  const lang = useLanguage();
  const sanityClient = useSanityClient({ useCdn: true });
  const builder = sanityImageUrlBuilder(sanityClient);

  if (!language) language = lang;

  const title = openGraph?.title?.localized || altTitle || undefined;
  const metaDescription =
    openGraph?.description?.localized || altDescription || undefined;
  const image = buildOpenGraphImageUrl(builder, openGraph?.image) || altImage;

  const metas = [
    {
      property: "og:type",
      content: "website"
    },
    {
      name: "twitter:card",
      content: "summary"
    },
    {
      name: "twitter:site",
      content: "@Silversea"
    }
  ];

  if (image) {
    metas.push({
      property: "og:image",
      content: image
    });
  }

  if (title) {
    metas.push(
      {
        name: "twitter:title",
        content: title
      },
      {
        property: "og:title",
        content: title
      }
    );
  }

  if (metaDescription) {
    metas.push(
      {
        name: "description",
        content: metaDescription
      },
      {
        property: "og:description",
        content: metaDescription
      },
      {
        name: "twitter:description",
        content: metaDescription
      }
    );
  }

  return (
    <Helmet title={title} titleTemplate={`%s | Silversea`} meta={metas}>
      {excludeFromSitemap && <meta name="robots" content="noindex" />}
      {children}
    </Helmet>
  );
};

export default OpenGraph;
